import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, ClipboardList } from 'lucide-react';
import { Button, Textarea, Label, cn } from '@tendedloop/ui';
import {
  type StatusItem,
  type StatusCategory,
  type StatusOption,
  STATUS_OPTIONS,
  formatStatus,
  getIconFromSeverity,
} from './types';

interface StatusUpdateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: StatusItem | null;
  onSubmit: (statusItemId: string, newStatus: string, comment?: string) => Promise<void>;
}

export function StatusUpdateModal({ open, onOpenChange, item, onSubmit }: StatusUpdateModalProps) {
  const { t } = useTranslation('visitor');
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when item changes or modal opens
  useEffect(() => {
    if (item && open) {
      setSelectedStatus(null);
      setComment('');
      setError(null);
    }
  }, [item, open]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onOpenChange]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open || !item) return null;

  const category = item.statusType.category as StatusCategory;

  // Use tree-provided options if available, otherwise fall back to hardcoded
  const options: StatusOption[] = item.statusOptions
    ? item.statusOptions.map((opt) => ({
        value: opt.value,
        labelKey: opt.name, // Use tree name directly as label
        descriptionKey: opt.name,
        icon: opt.icon || getIconFromSeverity(opt.severity, opt.isBadStatus),
      }))
    : STATUS_OPTIONS[category] || [];
  const useTreeLabels = !!item.statusOptions;

  const handleSubmit = async () => {
    if (!selectedStatus) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(item.id, selectedStatus, comment || undefined);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false);
    }
  };

  return (
    <>
      {/* Scrim — v5: converted from full-screen takeover to bottom sheet
          per 2/2 evaluator convergence. Matches IssueSheet / BuildingSheet /
          LanguageSwitcher pattern for cross-flow visual consistency. */}
      <button
        type="button"
        aria-hidden={!open}
        tabIndex={-1}
        onClick={handleClose}
        className="fixed inset-0 z-40 bg-black/55 transition-opacity"
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className={cn(
          'fixed inset-x-0 bottom-0 z-50 flex max-h-[85dvh] flex-col overflow-hidden bg-white',
          'rounded-t-[32px] pb-[calc(env(safe-area-inset-bottom)+1rem)]',
          'shadow-[0_-20px_60px_rgba(10,14,20,0.2)]',
        )}
      >
        {/* Drag handle — pure visual affordance matching IssueSheet */}
        <div aria-hidden="true" className="mx-auto mt-[18px] mb-3 h-1 w-10 flex-shrink-0 rounded-full bg-slate-200" />

        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between gap-3 px-5 pb-3">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10"
              aria-hidden="true"
            >
              {item.statusType.icon ? (
                <span className="text-2xl">{item.statusType.icon}</span>
              ) : (
                <ClipboardList className="h-5 w-5 text-primary" />
              )}
            </span>
            <div className="min-w-0">
              <h2
                id="modal-title"
                className="truncate text-[18px] font-bold leading-tight tracking-[-0.02em] text-[#0A0E14]"
                lang={item.statusType.nameLocale}
                dir="auto"
              >
                {item.statusType.displayName ?? item.statusType.name}
              </h2>
              <p className="truncate text-[15px] font-medium text-[#5A6472]">
                {t('status.current')}: <span className="capitalize">{formatStatus(item.currentStatus)}</span>
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full transition-transform active:scale-95"
            style={{ background: '#F3F4F0', color: '#1F2833' }}
            aria-label={t('common:buttons.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 pt-1">
          <p className="text-[16px] font-medium mb-4">{t('status.whatsTheStatus')}</p>

          <div className="space-y-3">
            {options.map((option) => {
              const isSelected = selectedStatus === option.value;
              const isCurrent = item.currentStatus === option.value;

              return (
                <button
                  key={option.value}
                  onClick={() => setSelectedStatus(option.value)}
                  disabled={isSubmitting}
                  className={cn(
                    'w-full p-4 rounded-xl border-2 text-start transition-all',
                    'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                    isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
                  )}
                  aria-pressed={isSelected}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center text-lg',
                        isSelected ? 'bg-primary/10' : 'bg-muted',
                      )}
                      aria-hidden="true"
                    >
                      {option.icon}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {useTreeLabels ? option.labelKey : t(`status.levels.${option.labelKey}`)}
                        </span>
                        {isCurrent && (
                          <span className="text-[15px] bg-muted px-2 py-0.5 rounded">{t('status.current')}</span>
                        )}
                      </div>
                      {!useTreeLabels && (
                        <p className="text-[15px] text-muted-foreground mt-0.5">
                          {t(`status.descriptions.${option.descriptionKey}`)}
                        </p>
                      )}
                    </div>
                    {isSelected && (
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <span className="text-primary-foreground text-[15px]">✓</span>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Comment field */}
          <div className="mt-4 space-y-2">
            <Label htmlFor="comment" className="text-[15px] font-medium">
              {t('feedback.addCommentLabel')}
            </Label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('feedback.addCommentPlaceholder')}
              rows={3}
              maxLength={500}
              disabled={isSubmitting}
              className="resize-none"
            />
            <p className="text-[15px] text-muted-foreground text-end">
              {t('feedback.characterCount', { count: comment.length, max: 500 })}
            </p>
          </div>

          {/* Error message */}
          {error && <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-lg text-[15px]">{error}</div>}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-[#E8EAED] bg-white px-5 pt-3">
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleClose} disabled={isSubmitting} className="flex-1 h-12">
              {t('common:buttons.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={!selectedStatus || isSubmitting} className="flex-1 h-12">
              {isSubmitting ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  {t('feedback.submitting')}
                </>
              ) : (
                t('status.updateCta', 'Update status')
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
